import React, { useEffect, useState } from 'react';
import { Grid, pn, useElementBreakpoints } from '@compass/core';
import {
    ToggleButtonGroup,
    ToggleButton,
    Autocomplete,
    TextField,
    Checkbox,
} from '@mui/material';
import { Select } from 'components';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
    StudentFilter as StudentFilterModel,
    StudentFilterResource,
    LkStudentDisplayFilterId,
    SimpleDto,
    StudentGroupBy,
} from 'generated/openApi';
import { useStudentData } from '../contexts/StudentDataContext';
import { StudentFilter } from '../types';
import ApiService from 'services/apiService';
import { pillToggleGroupStyles } from '../styles/segmentedControlStyles';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const StudentFilterControls = () => {
    const { breakpointsRef } = useElementBreakpoints();
    const { filter, updateFilter, activeSchoolYear } = useStudentData();

    const [resource, setResource] = useState<StudentFilterResource>({} as StudentFilterResource);
    const properties = resource.properties || [];

    useEffect(() => {
        ApiService.students.getFilterResource().then(setResource);
    }, []);

    const handleGroupByChange = (_: React.MouseEvent<HTMLElement>, newValue: StudentGroupBy | null) => {
        if (newValue === null) return;
        updateFilter({ groupBy: newValue });
    };

    const schoolYearField = () => {
        const property = properties.find(x => x.propertyName === pn<StudentFilterModel>("schoolYears"));
        return (
            <Select
                modelProperty={property}
                value={resource.schoolYearsWithActive?.find(x => x.id === (filter.schoolYear ?? activeSchoolYear))}
                options={resource.schoolYearsWithActive}
                getOptionLabel={(o: SimpleDto) => o.name}
                onChange={(e, v) => updateFilter({ schoolYear: v?.id })}
                size="small"
            />
        );
    };

    const multiYearField = () => {
        const yearOptions = resource.schoolYearsWithActive ?? [];
        const selected = yearOptions.filter(y => filter.schoolYears.includes(y.id!));
        return (
            <Autocomplete
                multiple
                size="small"
                options={yearOptions}
                value={selected}
                onChange={(_, v) => updateFilter({ schoolYears: v.map(i => i.id!) })}
                disableCloseOnSelect
                getOptionLabel={(o: SimpleDto) => o.name ?? ''}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                limitTags={1}
                renderOption={(props, option, { selected }) => (
                    <li {...props}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ mr: 1 }} checked={selected} />
                        {option.name}
                    </li>
                )}
                renderInput={(params) => <TextField {...params} label="School Years" placeholder="Select" />}
            />
        );
    };

    const renderFilter = (
        propName: keyof StudentFilterModel,
        valueId: number | undefined,
        options: any[] | undefined,
        updateKey: keyof StudentFilter
    ) => {
        const property = properties.find(x => x.propertyName === pn<StudentFilterModel>(propName));
        if (!property) return null;
        return (
            <Select
                modelProperty={property}
                value={options?.find(x => x.id === valueId)}
                options={options}
                getOptionLabel={(o) => o.name}
                onChange={(e, v) => updateFilter({ [updateKey]: v?.id })}
                size="small"
            />
        );
    };

    const showProgramFilter = resource.displayFilters?.some(x => x.id === LkStudentDisplayFilterId.Program);

    if (!resource.schoolYearsWithActive?.length) return null;

    return (
        <Grid
            ref={breakpointsRef}
            container
            rowSpacing={3}
            columnSpacing={2}
            sx={{ mb: 3 }}
        >
            <Grid item xs={12} md={4}>
                {filter.groupBy === StudentGroupBy.Year ? multiYearField() : schoolYearField()}
            </Grid>

            <Grid item xs={12} md={8}>
                <ToggleButtonGroup
                    value={filter.groupBy}
                    exclusive
                    onChange={handleGroupByChange}
                    size="small"
                    sx={pillToggleGroupStyles}
                >
                    <ToggleButton value={StudentGroupBy.Grade}>Grade</ToggleButton>
                    <ToggleButton value={StudentGroupBy.School}>School</ToggleButton>
                    <ToggleButton value={StudentGroupBy.Year}>Year</ToggleButton>
                </ToggleButtonGroup>
            </Grid>

            {showProgramFilter && (
                <Grid item xs={12} md={4}>
                    {renderFilter("programIds", filter.programId, resource.programs, "programId")}
                </Grid>
            )}

            <Grid item xs={12} md={4}>
                {renderFilter("schoolIds", filter.schoolId, resource.schools, "schoolId")}
            </Grid>

            <Grid item xs={12} md={4}>
                {renderFilter("gradeLevelIds", filter.gradeLevelId, resource.gradeLevels, "gradeLevelId")}
            </Grid>
        </Grid>
    );
};